import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import i18next from 'i18next';

import { CatalogueMenu } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { pdfService } from 'services/pdf.service';
import { productDataService } from 'services/products.service';

import { useCatalogueLabels } from 'hooks';

const useOptions = () => {
  const { control, register, watch, handleSubmit, setValue } = useForm();
  const { t } = useTranslation();
  const { genreLabel, categoryLabel } = useCatalogueLabels();

  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [isEmailSent, isEmailSentSet] = useState<boolean>(false);

  const genre = watch('genre');
  const category = watch('category');

  const { language } = i18next;

  const { warehouse, menu, season, marketLine, zone } = useAppSelector(
    (state) => state.settings
  );

  const userEmail = useAppSelector((state) => state.userData.user.email);
  const permissions = useAppSelector(getUserPermissions);

  const onSubmit = (data: FieldValues) => {
    isLoadingSet(true);
    isEmailSentSet(false);

    const targetEmail = data.email || userEmail;

    const params = {
      season,
      seasonLine: marketLine,
      zone,
      nodeName: data.genre === 'all' ? '' : data.genre,
      lang: language,
    };
    const categories = data.category.filter((item: string) => item !== 'ALL');

    productDataService
      .getPDFData(params, categories)
      .then((res) => {
        const canViewInactive = permissions.includes('VIEW_INACTIVE_ITEMS');

        const sortedData = res.data.map((categoryItem: any) => ({
          ...categoryItem,
          products: categoryItem.products
            ?.filter((product: any) => {
              if (canViewInactive) return true;
              return product.mainProduct?.active !== false;
            })
            ?.map((product: any) => {
              const colors =
                !canViewInactive && Array.isArray(product.colors)
                  ? product.colors.filter((c: { active?: boolean }) => c.active !== false)
                  : product.colors;

              return {
                ...product,
                colors,
                mainProduct: {
                  ...product.mainProduct,
                  // The PDF template prints mainProduct.nodeName as the product's category, like
                  // the single-product PDF does; the backend fills it with the genre code and
                  // puts the category code in nodeName2.
                  nodeName: product.mainProduct?.nodeName2
                    ? categoryLabel(product.mainProduct.nodeName2)
                    : product.mainProduct?.nodeName,
                  sizeMinQtyPrice: product.mainProduct?.sizeMinQtyPrice
                    ? [...product.mainProduct.sizeMinQtyPrice].sort(
                        (a: any, b: any) => (a.index || 0) - (b.index || 0)
                      )
                    : product.mainProduct?.sizeMinQtyPrice,
                },
              };
            }),
        }));

        const hasAnyProduct = sortedData.some(
          (categoryItem: any) => (categoryItem.products?.length ?? 0) > 0
        );
        if (!hasAnyProduct) {
          isLoadingSet(false);
          toast.error(
            t('pdf.noItemsAvailable', {
              defaultValue: 'No items available for this selection',
            })
          );
          return;
        }

        const pdfData = {
          season,
          seasonLine: marketLine,
          data: sortedData,
          email: targetEmail,
        };

        pdfService
          .getCataloguePDF(pdfData)
          .then(() => {
            isLoadingSet(false);
            isEmailSentSet(true);
            toast.success(
              t('pdf.generationStarted') ||
                'PDF generation started. You will receive an email with the download link shortly.'
            );
          })
          .catch(() => {
            isLoadingSet(false);
            toast.error(t('pdf.unableToCreate') || 'Unable to create PDF, please try again');
          });
      })
      .catch(() => {
        isLoadingSet(false);
        toast.error(t('pdf.unableToDownload') || 'Unable to download data, please try again');
      });
  };

  const seasonLineOptions = [{ value: marketLine, label: marketLine }];

  const genres = menu?.map((item: CatalogueMenu) => ({
    value: item.nodeName,
    label: genreLabel(item.nodeName),
  }));

  const categories = menu
    .filter((item: CatalogueMenu) => item.nodeName === genre)
    .map((item: CatalogueMenu) => item.nodeName2)
    .flat()
    .sort((a, b) => a.order - b.order);

  const genreOptions = [{ value: 'all', label: 'Whole catalogue' }].concat(genres);

  useEffect(() => {
    if (marketLine) {
      setValue('seasonLine', marketLine);
    }
  }, [warehouse]);

  useEffect(() => {
    if (genre) {
      setValue('category', []);
    }
  }, [genre]);

  useEffect(() => {
    if (userEmail) {
      setValue('email', userEmail);
    }
  }, [userEmail, setValue]);

  return {
    handleSubmit,
    onSubmit,
    warehouse,
    control,
    seasonLineOptions,
    genreOptions,
    genre,
    register,
    categories,
    isLoading,
    category,
    isEmailSent,
    setValue,
    marketLine,
  };
};

export { useOptions };
